import React, { useState, useEffect, useContext, useRef } from 'react';
import Text from '@amzn/meridian/text';
import { TEXTS } from '../../utils/constants';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import './AssociateViewTable.css';
import { ShiftAllocationContext } from '../context/ShiftAllocationContext';
import { ManagerViewTable } from './ManagerViewTable';
import { PROCESS_PATHS } from '../data';
import { createSlotIdGenerator } from './utils';
import AutoScroll from '../../utils/AutoScroll';
import { getLaborBoardScrollSpeed, setLaborBoardScrollSpeed } from '../../utils/networkUtil';

const SCROLL_SPEEDS = { slow: 10, normal: 7, fast: 5 };

function ShiftAllocationAssociatesView(props) {
  // Right now Data is taken from dummy file, pass as arg to getShiftAllocationAssociatesView
  const [associateInfoList, setAssociateInfoList] = useState([]);

  const [tables, setTables] = useState([[]]);
  const [scrollSpeed, setScrollSpeedState] = useState(() => {
    const saved = getLaborBoardScrollSpeed();
    return SCROLL_SPEEDS[saved] ? saved : 'normal';
  });
  const setScrollSpeed = (speed) => {
    setLaborBoardScrollSpeed(speed);
    setScrollSpeedState(speed);
  };
  const [maxRows, setMaxRows] = useState(0);
  const { shiftAllocationModel, shiftAllocationDispatch } = useContext(ShiftAllocationContext);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    //function to map associates to process paths
    function mapAssociatesToProcessPath() {
      const processInfo = shiftAllocationModel['processInfo'];
      const newList = [];

      processInfo
        .filter((boardEntry) => boardEntry.id !== PROCESS_PATHS.BENCH)
        .forEach((boardEntry) => {
          const slotLabels = boardEntry.slotLabels || {};
          // Use the SAME generator as ProcessDetailCard so slot ids match by construction. Advance
          // it for EVERY assignment per process (the card renders them all, filled first in this
          // same order), so the id computed here lines up with the card even when some associates
          // are missing enrichment info.
          const nextSlotIdForProcess = createSlotIdGenerator(boardEntry.id);
          boardEntry.assignments.forEach((assignment) => {
            const slotId = nextSlotIdForProcess(assignment.allocationAttrs.process);
            if (shiftAllocationModel['associateInfo'][assignment.resourceId]) {
              // Carry a planner's custom per-slot label over to the associate view. When set, the
              // label replaces only the trailing process segment of the path, keeping the main
              // path/title (e.g. "Diverter - <custom label>").
              const customLabel = slotLabels[slotId];
              newList.push({
                path: getPathName(boardEntry, assignment, customLabel),
                alias: assignment.resourceId,
                name: shiftAllocationModel['associateInfo'][assignment.resourceId].name || assignment.resourceId
              });
            } else {
              console.log('Associate Info not found', assignment.resourceId);
            }
          });
        });

      // sorting the list alphabetically based on name
      newList.sort((a, b) => {
        if (!isNaN(a.name)) return 1;
        if (!isNaN(b.name)) return -1;
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
      setAssociateInfoList(newList);
    }
    function getMaxRows() {
      const rowHeight = 50; // as thumbnail width is 48px
      const windowHeight = window.innerHeight - 250;
      const rows = Math.floor(windowHeight / rowHeight);
      setMaxRows(rows);
    }
    getMaxRows();
    mapAssociatesToProcessPath();
  }, [shiftAllocationModel]);

  useEffect(() => {
    updateTables();
  }, [maxRows, associateInfoList]);

  // Builds the associate's path label: "<title>[<aisle range>] - <trailing>". The trailing segment
  // is the planner's custom slot label when one is set, otherwise the default process name. This
  // keeps the main path/title (e.g. "Diverter - <custom label>") while mirroring the process card,
  // which swaps the slot's header for the custom label.
  const getPathName = (boardEntry, assignment, customLabel) => {
    let title = boardEntry.title;
    if (assignment.allocationAttrs.aisles) {
      const aisles = assignment.allocationAttrs.aisles;
      const numbers = aisles.map((aisle) => parseInt(aisle.split('-')[1]));
      const min = Math.min(...numbers);
      const max = Math.max(...numbers);
      title = `${title}${min}-${title}${max}`;
    }
    const trailing = customLabel != null ? customLabel : assignment.allocationAttrs.process;
    return `${title} - ${trailing}`;
  };

  const updateTables = () => {
    const rowsPerTable = maxRows;
    const totalRows = associateInfoList.length;

    let newTables = [[]];
    let tableIndex = 0;
    let currentRows = 0;

    for (let i = 0; i < totalRows; i++) {
      if (currentRows === rowsPerTable) {
        // Create a new table if the current table is full
        tableIndex++;
        currentRows = 0;
        newTables.push([]);
      }
      newTables[tableIndex].push(associateInfoList[i]);
      currentRows++;
    }
    setTables(newTables);
  };
  const onClickManagerView = () => {
    // Add call to manager view here
    props.history.replace('/shift-allocation');
  };

  return (
    <Column overflowX="scroll" spacingInset="400" overflowY="hidden">
      <Row alignmentHorizontal="justify">
        <Text type={TEXTS.H1}>Labor Board</Text>
        <Row spacing="500" alignmentVertical="center">
          <div className="labor-board-scroll-control">
            <Text type={TEXTS.T3}>Scroll:</Text>
            {Object.keys(SCROLL_SPEEDS).map((speed) => (
              <Button
                key={speed}
                size="small"
                type={scrollSpeed === speed ? 'secondary' : 'tertiary'}
                onClick={() => setScrollSpeed(speed)}>
                {speed.charAt(0).toUpperCase() + speed.slice(1)}
              </Button>
            ))}
          </div>
          <Button size="small" type="primary" onClick={onClickManagerView}>
            Manager View
          </Button>
        </Row>
      </Row>
      <Row
        className="labor-board-associate-view-table-row"
        spacing="100"
        overflowX="auto"
        overflowY="hidden"
        alignmentVertical="top"
        widths={'grid-4'}
        ref={tableContainerRef}>
        {tables.map((table, index) => (
          <Column
            key={index}
            className="labor-board-associate-view-table-column"
            alignmentHorizontal="center"
            spacingInset="none"
            overflowX={'scroll'}
            overflowY="hidden"
            type={'outline'}>
            <ManagerViewTable table={table}></ManagerViewTable>
          </Column>
        ))}
      </Row>
      <AutoScroll containerRef={tableContainerRef} dwellInSeconds={SCROLL_SPEEDS[scrollSpeed]} />
    </Column>
  );
}

export default ShiftAllocationAssociatesView;
